import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import type { MediaStorageStatus } from '@/lib/mediaStorage';

export type SessionCapabilities = {
  manage_sites: boolean;
  manage_own_sites: boolean;
  switch_site: boolean;
  manage_users: boolean;
  purge_history: boolean;
  system_overview: boolean;
  view_cron_urls: boolean;
};

const defaultCaps: SessionCapabilities = {
  manage_sites: false,
  manage_own_sites: false,
  switch_site: false,
  manage_users: false,
  purge_history: false,
  system_overview: false,
  view_cron_urls: false,
};

type SessionResp = {
  ok: boolean;
  user?: { username: string; role?: string; account_valid_until?: string | null };
  capabilities?: Partial<SessionCapabilities>;
  needs_own_site?: boolean;
  owned_sites_count?: number | null;
  /** 普通用户自建站点上限；管理员为 undefined */
  max_owned_sites?: number | null;
  media_storage?: MediaStorageStatus | null;
  current_site?: { id: number; site_code: string; site_name: string } | null;
};

export function useSession() {
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('admin');
  const [currentSiteName, setCurrentSiteName] = useState('');
  const [currentSiteId, setCurrentSiteId] = useState(0);
  const [currentSiteCode, setCurrentSiteCode] = useState('');
  const [needsOwnSite, setNeedsOwnSite] = useState(false);
  const [ownedSitesCount, setOwnedSitesCount] = useState<number | null>(null);
  const [maxOwnedSites, setMaxOwnedSites] = useState<number | null>(null);
  const [accountValidUntil, setAccountValidUntil] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<SessionCapabilities>(defaultCaps);
  const [mediaStorage, setMediaStorage] = useState<MediaStorageStatus | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch<SessionResp>('session', { method: 'GET' });
      if (r.ok && r.user?.username) {
        setOk(true);
        setUsername(r.user.username);
        setRole(r.user.role === 'user' ? 'user' : 'admin');
        const cs = r.current_site;
        setCurrentSiteName(cs?.site_name || '');
        setCurrentSiteId(cs?.id ?? 0);
        setCurrentSiteCode(cs?.site_code || '');
        setNeedsOwnSite(r.needs_own_site === true);
        setOwnedSitesCount(typeof r.owned_sites_count === 'number' ? r.owned_sites_count : null);
        setMaxOwnedSites(typeof r.max_owned_sites === 'number' ? r.max_owned_sites : null);
        const av = r.user?.account_valid_until;
        setAccountValidUntil(av !== undefined && av !== null && av !== '' ? String(av).slice(0, 10) : null);
        const c = r.capabilities;
        setCapabilities({
          manage_sites: c?.manage_sites === true,
          manage_own_sites: c?.manage_own_sites === true,
          switch_site: c?.switch_site === true,
          manage_users: c?.manage_users === true,
          purge_history: c?.purge_history === true,
          system_overview: c?.system_overview === true,
          view_cron_urls: c?.view_cron_urls === true,
        });
        setMediaStorage(r.media_storage ?? null);
      } else {
        setOk(false);
        setUsername('');
        setRole('admin');
        setCurrentSiteName('');
        setCurrentSiteId(0);
        setCurrentSiteCode('');
        setNeedsOwnSite(false);
        setOwnedSitesCount(null);
        setMaxOwnedSites(null);
        setAccountValidUntil(null);
        setCapabilities(defaultCaps);
        setMediaStorage(null);
      }
    } catch {
      setOk(false);
      setUsername('');
      setRole('admin');
      setCurrentSiteName('');
      setCurrentSiteId(0);
      setCurrentSiteCode('');
      setNeedsOwnSite(false);
      setOwnedSitesCount(null);
      setMaxOwnedSites(null);
      setAccountValidUntil(null);
      setCapabilities(defaultCaps);
      setMediaStorage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return useMemo(
    () => ({
      ok,
      loading,
      username,
      role,
      currentSiteName,
      currentSiteId,
      currentSiteCode,
      needsOwnSite,
      ownedSitesCount,
      maxOwnedSites,
      accountValidUntil,
      capabilities,
      mediaStorage,
      refresh,
    }),
    [
      ok,
      loading,
      username,
      role,
      currentSiteName,
      currentSiteId,
      currentSiteCode,
      needsOwnSite,
      ownedSitesCount,
      maxOwnedSites,
      accountValidUntil,
      capabilities,
      mediaStorage,
      refresh,
    ]
  );
}
